import React, { useState } from 'react';
import { FaServer, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import { adminService } from '../../services/adminService';

// Customer-detail "Provisioning" section. Records HOW a customer's NBN service was
// provisioned — 'auto' (via the admin portal FIBRE::Create) or 'manual' (by hand in the
// Vocus portal) — AFTER provisioning has succeeded. This is a record only; it does NOT
// submit any Vocus order. Also used to back-fill the existing manually-provisioned base so
// those customers stop showing "not provisioned".
const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const ProvisioningSection = ({ customer, updateLocalCustomer }) => {
  const status = customer?.provisionStatus || null;
  const method = customer?.provisionMethod || null;
  const isProvisioned = status === 'active' || status === 'pending';

  const [provMethod, setProvMethod] = useState('manual');
  const [serviceId, setServiceId] = useState(customer?.vocusServiceId || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const statusLabel = !status
    ? 'Not provisioned'
    : status === 'pending'
      ? `Provisioning… (${method ? titleCase(method) : 'Auto'})`
      : `Provisioned${method ? ` (${titleCase(method)})` : ''}`;

  const submit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminService.markProvisioned({
        customerId: customer.UID,
        provisionMethod: provMethod,
        ...(serviceId.trim() ? { vocusServiceId: serviceId.trim() } : {}),
      });
      if (res && res.success) {
        setDone(true);
        if (typeof updateLocalCustomer === 'function') {
          updateLocalCustomer({
            ...customer,
            provisionStatus: 'active',
            provisionMethod: provMethod,
            vocusServiceId: serviceId.trim() || customer.vocusServiceId || null,
          });
        }
      } else {
        setError((res && res.error) || 'Failed to record provisioning');
      }
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-6 border-t border-[#3c3836] mt-6">
      <h3 className="text-[#ebdbb2] font-semibold mb-3 flex items-center gap-2">
        <FaServer className="text-[#83a598]" /> Provisioning
      </h3>
      <p className="text-sm mb-3">
        Status:{' '}
        <span className={`font-medium ${isProvisioned ? 'text-[#b8bb26]' : 'text-[#fabd2f]'}`}>
          {statusLabel}
        </span>
        {customer?.vocusServiceId ? (
          <span className="text-[#928374]"> · Service ID: {customer.vocusServiceId}</span>
        ) : null}
      </p>

      {!isProvisioned && !done && (
        <div className="bg-[#1d2021] border border-[#3c3836] rounded-lg p-4 space-y-3">
          <p className="text-sm text-[#928374]">
            Record how this service was provisioned (after you&apos;ve provisioned it via the admin
            portal or the Vocus portal). This does not submit a Vocus order.
          </p>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-[#ebdbb2] cursor-pointer">
              <input
                type="radio"
                name="provMethod"
                value="manual"
                checked={provMethod === 'manual'}
                onChange={() => setProvMethod('manual')}
              />
              Manual (Vocus portal)
            </label>
            <label className="flex items-center gap-2 text-sm text-[#ebdbb2] cursor-pointer">
              <input
                type="radio"
                name="provMethod"
                value="auto"
                checked={provMethod === 'auto'}
                onChange={() => setProvMethod('auto')}
              />
              Auto (admin portal)
            </label>
          </div>
          <input
            type="text"
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            placeholder="Vocus Service ID (optional, e.g. SVC0123456)"
            className="w-full px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-[#ebdbb2] placeholder-[#665c54] text-sm focus:outline-none focus:border-[#83a598]"
          />
          {error && <p className="text-sm text-[#fb4934]">{error}</p>}
          <button
            onClick={submit}
            disabled={loading}
            className="px-5 py-2 rounded-lg font-medium bg-[#83a598] text-[#1d2021] hover:bg-[#689d6a] transition-colors disabled:opacity-60 flex items-center gap-2"
          >
            {loading ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} Mark as Provisioned
          </button>
        </div>
      )}

      {done && (
        <p className="text-sm text-[#b8bb26] flex items-center gap-2">
          <FaCheckCircle /> Provisioning recorded as {provMethod}.
        </p>
      )}
    </div>
  );
};

export default ProvisioningSection;
